import React, { useState, useEffect, useCallback } from 'react';
import PasienLayout from './PasienLayout'; 
import axiosInstance from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import './pasien-pages.css';
import { normalizeProfile } from './pasien-helpers';

const normalizeAppointment = (item) => ({
  id: item.id,
  date: item.tanggal_pendaftaran
    ? new Date(item.tanggal_pendaftaran).toLocaleDateString('id-ID', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    : '-',
  time: item.jam_kunjungan ? item.jam_kunjungan.substring(0, 5) : '-',
  doctorName: item.dokter?.nama ?? '-',
  specialty: item.dokter?.spesialisasi ?? '-',
  status: item.status ?? 'pending',
});

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, apptRes] = await Promise.all([
        axiosInstance.get('/pasien/profile'),
        axiosInstance.get('/pasien/appointments'),
      ]);

      const profileBody = profileRes.data?.data ?? profileRes.data;
      setProfile(normalizeProfile(profileBody, user));

      const rawList = apptRes.data?.data ?? apptRes.data ?? [];
      setAppointments(rawList.map(normalizeAppointment));
    } catch (err) {
      console.error(err);
      setError('Gagal memuat data dashboard.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 🟢 BERUBAH: Sekarang mengembalikan objek inline style untuk menjamin warna keluar
  const getBadgeStyle = (status) => {
    const s = String(status).toLowerCase().trim();
    
    // Base style untuk bentuk kotak badge bulat lonjong yang rapi
    const baseStyle = {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '50px',
      fontSize: '12px',
      fontWeight: '600',
      textAlign: 'center',
    };

    if (s === 'selesai' || s === 'completed') {
      return { ...baseStyle, backgroundColor: '#dcfce7', color: '#15803d' }; // Kotak Hijau Muda, Teks Hijau Tua
    }
    if (s === 'dibatalkan' || s === 'cancelled' || s === 'rejected') {
      return { ...baseStyle, backgroundColor: '#fee2e2', color: '#b91c1c' }; // Kotak Merah Muda, Teks Merah Tua
    }
    if (s === 'check-in' || s === 'check_in' || s === 'checked_in' || s === 'periksa') {
      return { ...baseStyle, backgroundColor: '#dbeafe', color: '#1d4ed8' }; // Kotak Biru Muda, Teks Biru Tua
    }
    
    // Default / Pending
    return { ...baseStyle, backgroundColor: '#fef3c7', color: '#b45309' }; // Kotak Kuning/Amber, Teks Oranye Tua
  };

  // Menerjemahkan teks status agar rapi dilihat pasien
  const getStatusLabel = (status) => {
    const s = String(status).toLowerCase().trim();
    if (s === 'pending') return 'Menunggu';
    if (s === 'cancelled' || s === 'dibatalkan') return 'Dibatalkan';
    if (s === 'completed' || s === 'selesai') return 'Selesai';
    if (s === 'check_in' || s === 'checked_in' || s === 'check-in') return 'Check-In';
    return status;
  };

  return (
    <PasienLayout>
      <div className="w-full p-4 md:p-6" style={{ width: '100%', boxSizing: 'border-box' }}>
        
        {/* ==========================================================================
           1. HEADER & ACTIONS
           ========================================================================== */}
        <header className="pasien-page-header" style={{ marginBottom: '20px' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'stretch' : 'flex-start',
            gap: '15px'
          }}>
            <div>
              <h1 style={{ fontSize: isMobile ? '1.5rem' : '2rem', margin: '0 0 4px 0', fontWeight: 700, color: '#1e293b' }}>
                Halo, {profile?.name || 'Pasien'} 👋
              </h1>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                Selamat datang kembali. Berikut adalah ringkasan kesehatan Anda.
              </p>
            </div>
            <div className="header-actions">
              <Link
                to="/"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  textDecoration: 'none', 
                  color: '#fff', 
                  padding: '10px 16px', 
                  borderRadius: '6px', 
                  backgroundColor: '#64748b', 
                  fontSize: '14px', 
                  fontWeight: 500, 
                  textAlign: 'center'
                }}
              >
                Ke Beranda
              </Link>
            </div>
          </div>
        </header>

        {error && (
          <div style={{ color: 'red', marginBottom: '20px', padding: '10px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading" style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
            Memuat informasi dashboard...
          </div>
        ) : (
          <div className="dashboard-container">
            
            {/* ==========================================================================
               2. GRID STATISTIK KARTU
               ========================================================================== */}
            <div className="pasien-stats-grid" style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 640 ? '1fr' : '1fr 1fr',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div className="stat-card" style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fff' }}>
                <span style={{ color: '#64748b', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Total Janji Temu</span>
                <h3 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#1e293b' }}>{appointments.length}</h3>
              </div>
              <div className="stat-card" style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fff' }}>
                <span style={{ color: '#64748b', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Status Akun</span>
                <h3 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#059669' }}>Aktif</h3>
              </div>
            </div>

            {/* ==========================================================================
               3. JADWAL KUNJUNGAN TERDEKAT
               ========================================================================== */}
            <div className="dashboard-section" style={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '16px' }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: window.innerWidth < 480 ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: window.innerWidth < 480 ? 'stretch' : 'center', 
                gap: '12px',
                marginBottom: '20px' 
              }}>
                <h2 style={{ fontSize: '1.15rem', color: '#111827', margin: 0, fontWeight: 600 }}>
                  Jadwal Kunjungan Terdekat
                </h2>
                <Link to="/pasien/daftar-berobat" className="btn-primary" style={{ textAlign: 'center', padding: '8px 16px' }}>
                  + Buat Janji
                </Link>
              </div>

              {appointments.length > 0 ? (
                <div>
                  {/* TAMPILAN DESKTOP (TABEL) */}
                  <div className="hidden md:block pasien-table-wrap">
                    <table className="pasien-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                          <th style={{ padding: '12px 8px' }}>Tanggal Kunjungan</th>
                          <th style={{ padding: '12px 8px' }}>Dokter</th>
                          <th style={{ padding: '12px 8px' }}>Spesialis</th>
                          <th style={{ padding: '12px 8px' }}>Jam</th>
                          <th style={{ padding: '12px 8px' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointments.map((a) => (
                          <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '12px 8px', fontWeight: 600 }}>{a.date}</td>
                            <td style={{ padding: '12px 8px' }}>{a.doctorName}</td>
                            <td style={{ padding: '12px 8px', color: '#6b7280' }}>{a.specialty}</td>
                            <td style={{ padding: '12px 8px' }}>{a.time} WIB</td>
                            <td style={{ padding: '12px 8px' }}>
                              {/* 🟢 BERUBAH DI SINI: Menggunakan inline style dari fungsi baru */}
                              <span style={getBadgeStyle(a.status)}>
                                {getStatusLabel(a.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* TAMPILAN MOBILE (KARTU) */}
                  <div className="block md:hidden" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {appointments.map((a) => (
                      <div key={a.id} style={{ 
                        border: '1px solid #f1f5f9', 
                        borderRadius: '8px', 
                        padding: '14px', 
                        backgroundColor: '#f8fafc',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{a.date}</span>
                          {/* 🟢 BERUBAH DI SINI JUGA: Sinkronisasi untuk tampilan mobile */}
                          <span style={getBadgeStyle(a.status)}>
                            {getStatusLabel(a.status)}
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#334155' }}>
                          <div><strong style={{ color: '#64748b' }}>Dokter:</strong> {a.doctorName}</div>
                          <div><strong style={{ color: '#64748b' }}>Spesialis:</strong> {a.specialty}</div>
                          <div><strong style={{ color: '#64748b' }}>Waktu:</strong> {a.time} WIB</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="pasien-empty" style={{ textAlign: 'center', padding: '30px 10px' }}>
                  <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>Sepertinya Anda belum memiliki jadwal janji temu dengan dokter.</p>
                  <Link to="/pasien/daftar-berobat" className="btn-primary" style={{ display: 'inline-block', padding: '10px 20px' }}>
                    Daftar Berobat Sekarang
                  </Link>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </PasienLayout>
  );
}